import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sun, Shield, CheckCircle, AlertTriangle, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function SPFGuide() {
  const navigate = useNavigate();

  const applicationAreas = [
    { area: "Face", amount: "Nickel-sized", note: "Don't forget temples and eyelids" },
    { area: "Neck", amount: "Nickel-sized", note: "Front and back, often forgotten" },
    { area: "Ears", amount: "Pea-sized each", note: "Tops, lobes, and behind" },
    { area: "Chest", amount: "Quarter-sized", note: "If exposed by clothing" },
    { area: "Hands", amount: "Pea-sized each", note: "Backs of hands age quickly" }
  ];

  const spfTypes = [
    { type: "Chemical SPF", pros: ["Lightweight", "No white cast", "Good under makeup"], cons: ["Can irritate sensitive skin", "Takes 15-20 min to activate"] },
    { type: "Mineral/Physical SPF", pros: ["Gentle on skin", "Works immediately", "Better for sensitive skin"], cons: ["Can leave white cast", "May feel heavier"] },
    { type: "Hybrid SPF", pros: ["Best of both worlds", "Elegant texture", "Broad protection"], cons: ["May be pricier", "Fewer options"] }
  ];

  const myths = [
    { myth: "I don't need SPF on cloudy days", truth: "Up to 80% of UV rays penetrate clouds" },
    { myth: "SPF in makeup is enough", truth: "You'd need 7x the normal amount of foundation for adequate protection" },
    { myth: "Higher SPF is always better", truth: "SPF 30 blocks 97% of rays, SPF 50 blocks 98% - proper application matters more" },
    { myth: "I have dark skin, I don't need SPF", truth: "All skin tones can experience UV damage and hyperpigmentation" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4">
            Week 3 • Day 3 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            SPF Application Practice
          </h1>
          <p className="text-xl text-secondary-600">Your #1 Anti-Aging Secret</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-amber-400 to-orange-400 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Sun className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">The Most Important Step</h3>
              <p className="text-amber-100 text-lg">
                SPF prevents 90% of premature aging and reduces skin cancer risk by 50%. 
                It's more important than any serum, cream, or treatment. Make it non-negotiable.
              </p>
            </div>
          </div>
        </section>

        {/* Application Guide */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">How Much to Apply</h2>
          <div className="space-y-3">
            {applicationAreas.map((item, index) => (
              <div key={index} className="flex items-center gap-4 p-4 bg-amber-50 rounded-xl">
                <Shield className="w-6 h-6 text-amber-500 flex-shrink-0" />
                <div className="flex-1">
                  <div className="flex justify-between items-center">
                    <h3 className="font-bold text-secondary-900">{item.area}</h3>
                    <span className="text-sm bg-amber-200 text-amber-700 px-3 py-1 rounded-full">{item.amount}</span>
                  </div>
                  <p className="text-sm text-secondary-600">{item.note}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* SPF Types */}
        <section className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border-2 border-amber-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">Types of Sunscreen</h2>
          <div className="space-y-4">
            {spfTypes.map((spf, index) => (
              <div key={index} className="bg-white rounded-xl p-5">
                <h3 className="text-lg font-bold text-amber-700 mb-3">{spf.type}</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-semibold text-green-600 mb-2">Pros:</p>
                    <ul className="space-y-1">
                      {spf.pros.map((pro, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-secondary-700">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-amber-600 mb-2">Cons:</p>
                    <ul className="space-y-1">
                      {spf.cons.map((con, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-secondary-700">
                          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Reapplication */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <Clock className="w-6 h-6 text-amber-500" />
            When to Reapply
          </h2>
          <ul className="space-y-3">
            {[
              "Every 2 hours if outdoors",
              "Immediately after swimming or sweating",
              "After towel drying",
              "If you've touched your face frequently",
              "For indoor days, morning application is usually sufficient"
            ].map((tip, i) => (
              <li key={i} className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg text-secondary-700">
                <CheckCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                {tip}
              </li>
            ))}
          </ul>
        </section>

        {/* Myths */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6">SPF Myths Busted</h2>
          <div className="space-y-4">
            {myths.map((item, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-xl">
                <p className="text-red-600 font-medium line-through mb-1">Myth: {item.myth}</p>
                <p className="text-green-700"><strong>Truth:</strong> {item.truth}</p>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

export default SPFGuide;
